import React from 'react'
import { Button, Skeleton } from '@/shared/ui'
import { Table, Tbody, Td, Th, Thead, Tr } from '@/shared/ui/Table'
import { useToast } from '@/shared/ui/Toast'
import type { FinanceCostCategory, FinanceExpense } from '../../services/financeCostsService'
import { deleteFinanceExpense } from '../../services/financeCostsService'
import { formatMoneyFromCents } from '../../utils/money'
import { FinanceExpenseModal } from './FinanceExpenseModal'

type Props = {
  monthStartDate: string
  categories: FinanceCostCategory[]
  items: FinanceExpense[]
  loading: boolean
  onRefresh: () => Promise<void>
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleDateString('pt-BR') } catch { return iso }
}

export function FinanceExpensesPanel({ monthStartDate, categories, items, loading, onRefresh }: Props) {
  const { add: toast } = useToast()
  const [open, setOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<FinanceExpense | null>(null)
  const [deletingId, setDeletingId] = React.useState<string | null>(null)

  const catById = React.useMemo(() => new Map(categories.map((c) => [c.id, c.name])), [categories])

  async function onDelete(id: string) {
    const row = items.find((i) => i.id === id)
    const ok = window.confirm(`Excluir a despesa "${row?.name || '—'}"?`)
    if (!ok) return
    try {
      setDeletingId(id)
      await deleteFinanceExpense(id)
      toast({ title: 'Despesa excluída', variant: 'success', soft: true })
      await onRefresh()
    } catch (e: any) {
      toast({ title: 'Falha ao excluir', description: e?.message || String(e), variant: 'error', soft: true })
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-foreground/70">Despesas avulsas do mês (competência).</div>
        <Button onClick={() => { setEditing(null); setOpen(true) }}>Nova despesa</Button>
      </div>

      <div className="rounded-2xl border border-slate-800/80 bg-slate-950/80 p-4">
        {loading ? (
          <Skeleton className="h-28 w-full" />
        ) : (
          <div className="overflow-auto">
            <Table>
              <Thead>
                <Tr>
                  <Th>Nome</Th>
                  <Th>Categoria</Th>
                  <Th className="text-right">Valor</Th>
                  <Th>Pago em</Th>
                  <Th className="text-right">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((r) => (
                  <Tr key={r.id}>
                    <Td className="min-w-[240px] font-medium">
                      <div>{r.name}</div>
                      {r.notes ? <div className="text-xs text-foreground/70">{r.notes}</div> : null}
                    </Td>
                    <Td className="min-w-[160px]">{r.category_id ? (catById.get(r.category_id) || '-') : '-'}</Td>
                    <Td className="text-right whitespace-nowrap">{formatMoneyFromCents(r.amount_cents, r.currency)}</Td>
                    <Td className="whitespace-nowrap">{fmtDate(r.paid_at)}</Td>
                    <Td className="text-right whitespace-nowrap">
                      <div className="inline-flex gap-2">
                        <Button className="h-8 px-3" variant="outline" onClick={() => { setEditing(r); setOpen(true) }}>Editar</Button>
                        <Button className="h-8 px-3" variant="outline" disabled={deletingId === r.id} onClick={() => onDelete(r.id)}>
                          {deletingId === r.id ? 'Excluindo...' : 'Excluir'}
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
                {!items.length && (
                  <Tr>
                    <Td colSpan={5} className="text-sm text-foreground/70">Nenhuma despesa neste mês.</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </div>
        )}
      </div>

      <FinanceExpenseModal
        open={open}
        onClose={() => setOpen(false)}
        onSaved={onRefresh}
        categories={categories}
        initial={editing}
        defaultMonthStartDate={monthStartDate}
      />
    </div>
  )
}

